/**
 * Shared Field Mode photo-capture helpers.
 *
 * Compresses camera captures down to a sync-friendly JPEG and converts them
 * to data URLs so they can be queued through `enqueueOfflineFieldEvidence`.
 * Used by both the worker workspace and the supervisor inspection screens —
 * keep the compression settings here so every field photo behaves the same.
 */

export interface PhotoEvidencePayload {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  dataUrl: string;
}

export const fileToDataUrl = (file: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

export const compressPhoto = async (file: File) => {
  const maxDimension = 1280;
  const quality = 0.72;
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image could not be loaded'));
      img.src = objectUrl;
    });

    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas is not available');
    context.drawImage(image, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error('Image could not be compressed'));
      }, 'image/jpeg', quality);
    });

    return {
      blob,
      fileName: (file.name || `task-photo-${Date.now()}.jpg`).replace(/\.[^.]+$/, '.jpg'),
      mimeType: 'image/jpeg',
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/** Compress a capture and return the payload shape `enqueueOfflineFieldEvidence` expects. */
export const photoEvidencePayload = async (file: File, fileName?: string): Promise<PhotoEvidencePayload> => {
  const compressed = await compressPhoto(file);
  const dataUrl = await fileToDataUrl(compressed.blob);
  return {
    fileName: fileName ?? compressed.fileName,
    mimeType: compressed.mimeType,
    sizeBytes: compressed.blob.size,
    dataUrl,
  };
};
